import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Footer1 from "../components/Footer1";
import MobileNav from "../components/MobileNav";
import "./AllPrints.css";
import paintingsData from "../data/paintings.json";

const AllPrints = () => {
    const [prints, setPrints] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // --- NEW INTERACTIVE STATES ---
    const [activeFilter, setActiveFilter] = useState("All Archives");
    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'exhibition'
    
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);

        // 1. Lock screen scroll & set loading to true
        setLoading(true);
        document.body.style.overflow = "hidden";

        // 2. Fetch the data immediately so the DOM can build in the background
        setPrints(paintingsData); 

        // 3. Keep the aesthetic loader on screen for 1.8 seconds while images fetch in background
        const timer = setTimeout(() => {
            setLoading(false);
            document.body.style.overflow = "auto"; // Unlock scroll
        }, 1500);

        return () => {
            clearTimeout(timer);
            document.body.style.overflow = "auto";
        };
    }, []);

    // Dynamically generate filter categories based on the JSON data
    const categories = ["All Archives", "Mythological", "Positive Energy", "Singular Works"];

    // Filter logic
    const filteredPrints = useMemo(() => {
        if (activeFilter === "All Archives") return prints;
        if (activeFilter === "Singular Works") return prints.filter(p => !p.series || p.series === "");
        return prints.filter(p => p.category === activeFilter);
    }, [activeFilter, prints]);

    // Animations
    const fadeUp = {
        hidden: { opacity: 0, y: 30, scale: 0.98 },
        visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.2 } }
    };

    return (
        <div className="elite-prints-page">

            {/* --- AESTHETIC BACKGROUND LOADER --- */}
            <AnimatePresence>
                {loading && (
                    <motion.div
                        key="elite-loader"
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                            position: "fixed",
                            inset: 0,
                            zIndex: 99999,
                            backgroundColor: "#faf9f6", // Matches prints background
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <motion.span
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                            style={{
                                fontFamily: "'Playfair Display', serif", // Matches your CSS typography
                                fontSize: "1.2rem",
                                textTransform: "uppercase",
                                letterSpacing: "6px",
                                color: "#111111",
                                marginBottom: "20px"
                            }}
                        >
                            Curating Print Archive
                        </motion.span>
                        <div style={{ width: "80px", height: "1px", backgroundColor: "rgba(0,0,0,0.05)", overflow: "hidden", position: "relative" }}>
                            <motion.div
                                initial={{ x: "-100%" }}
                                animate={{ x: "100%" }}
                                transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#88b019", // Print section accent green
                                    position: "absolute"
                                }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <MobileNav />

            {/* --- TOP BANNER --- */}
            {/* Renders instantly in the DOM behind the loader */}
            <div className="elite-prints-banner"
                style={{ backgroundImage: "url('https://res.cloudinary.com/wbwvb4sk/image/upload/v1786597541/the_surya_3.jpg" }}
            >
                <div className="banner-overlay-soft"></div>
                
                {/* Tied to `!loading` so it animates IN after the loader finishes */}
                <motion.div className="banner-text-content" initial="hidden" animate={!loading ? "visible" : "hidden"} variants={fadeUp}>
                    <span className="banner-eyebrow">Archival Reproductions</span>
                    <h1 className="banner-main-title">The Print Archive</h1>
                    <div className="banner-delicate-line"></div>
                    <p className="banner-description">
                        Museum-grade limited editions of Anjaan Vivek’s most celebrated works.
                    </p>
                </motion.div>
            </div>

            <main className="elite-prints-main">
                {/* --- CURATORIAL TOOLBAR (Filter & View Toggle) --- */}
                <div className="curatorial-toolbar">
                    <div className="filter-group">
                        {categories.map((category) => (
                            <button 
                                key={category} 
                                className={`filter-btn ${activeFilter === category ? "active" : ""}`}
                                onClick={() => setActiveFilter(category)}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    <div className="view-toggle-group">
                        <span className="view-label">Perspective:</span>
                        <button 
                            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`} 
                            onClick={() => setViewMode('grid')}
                            title="Grid View"
                        >
                            <i className="fa-solid fa-table-cells-large"></i>
                        </button>
                        <button 
                            className={`toggle-btn ${viewMode === 'exhibition' ? 'active' : ''}`} 
                            onClick={() => setViewMode('exhibition')}
                            title="Exhibition View"
                        >
                            <i className="fa-solid fa-bars-staggered"></i>
                        </button>
                    </div>
                </div>

                {/* DOM elements render instantly and invisibly fetch images in the background */}
                {filteredPrints.length === 0 ? (
                    <div className="elite-empty-state">
                        <p>No masterworks found in this curation.</p>
                        <button onClick={() => setActiveFilter("All Archives")} className="btn-elite-secondary">Reset Curation</button>
                    </div>
                ) : (
                    <motion.div 
                        className={`prints-layout-container ${viewMode === 'exhibition' ? 'layout-exhibition' : 'layout-grid'}`}
                        initial="hidden"
                        animate={!loading ? "visible" : "hidden"} // Animates ON after loader
                        variants={staggerContainer}
                        key={viewMode + activeFilter} // Forces re-animation when changing views/filters
                    >
                        <AnimatePresence>
                            {filteredPrints.map((print) => {
                                // Default image is index 1. If not available, fallback to index 0.
                                const defaultImage = print.images?.[1]?.imageUrl || print.images?.[0]?.imageUrl || "https://via.placeholder.com/600x800?text=Masterpiece";
                                // Hover image is index 0
                                const hoverImage = print.images?.[0]?.imageUrl || null;
                                
                                return (
                                    <motion.div 
                                        key={print.id} 
                                        className="print-item-wrapper"
                                        variants={fadeUp}
                                        layout
                                    >
                                        <div className="print-image-wrapper" onClick={() => navigate(`/printDetails/${print.id}`)}>
                                            <img src={defaultImage} alt={print.title} className="print-img default-img" />
                                            
                                            {/* Render hover image if it exists and is different from the default */}
                                            {hoverImage && hoverImage !== defaultImage && (
                                                <img src={hoverImage} alt={`${print.title} Alternate`} className="print-img hover-img" />
                                            )}
                                        </div>
                                        
                                        <div className="print-meta">
                                            <h3 className="archive-print-title" onClick={() => navigate(`/printDetails/${print.id}`)}>{print.title}</h3>
                                            <span className="print-series">{print.series ? `The ${print.series} Anthology` : "Singular Masterwork"}</span>
                                            
                                            {/* ✅ PRICE FOR GRID VIEW */}
                                            {viewMode === 'grid' && (
                                                <div className="grid-price-tag">
                                                    <span>From ₹ 25,000</span>
                                                </div>
                                            )}

                                            {/* Extra details & Price only visible in Exhibition Mode */}
                                            {viewMode === 'exhibition' && (
                                                <div className="exhibition-details">
                                                    <div className="exhibition-divider"></div>
                                                    <p className="exhibition-story">{print.story || "A profound exploration of narrative and form."}</p>
                                                    <div className="exhibition-specs">
                                                        <span>Original: {print.material}</span>
                                                        <span className="spec-dot">•</span>
                                                        <span>{print.year}</span>
                                                    </div>
                                                    
                                                    {/* ✅ PRICE FOR EXHIBITION VIEW */}
                                                    <div className="exhibition-price-tag">
                                                        <span>Acquisition From ₹ 25,000</span>
                                                    </div>

                                                    <button className="btn-explore-exhibition" onClick={() => navigate(`/printDetails/${print.id}`)}>
                                                        View Acquisition Details
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </motion.div>
                )}
            </main>

            <Footer1 />
        </div>
    );
};

export default AllPrints;